import { Checkbox, Grid, Stack, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useParams, useNavigate } from 'react-router-dom';

import { isWithinTokenLimit } from 'gpt-tokenizer';

import { ReactComponent as BackIcon } from 'common/assets/images/button-previous.svg';
import { ReactComponent as HeaderIcon } from 'common/assets/images/reg-table-landing-icon.svg';
import AccordionComp from 'common/components/Accordion/AccordionComp';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import {
	AI_SIMULATION_TYPES,
	COPYANDPASTE_TEXT,
	DEFAULT_AI_SUMMARY_MAX_LIMIT,
} from 'common/constants/AISimulationConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { setRegulationText } from 'common/store/reducer/simulationReducer';
import {
	toSentenceCase,
	urlWithTenant,
	removeHTMLTags,
} from 'common/utils/utilFunctions';

import { useStringFeatureFlag } from 'hooks/useFeatureFlags';

import { getAiSimulationTitle } from 'features/AiSimulation/AiSimulationUtil';
import { getCitationDetails } from 'features/RegulationInventory/api';
import {
	ICitationDetailsElements,
	ISelectedCitationGroups,
	ISubPartCitationDetails,
} from 'features/RegulationInventory/api/type-def';
import CitationDescription from 'features/RegulationInventory/Components/CitationDescription/CitationDescription';
import {
	checkAllChildrenSelected,
	checkAllCitationForRegulation,
	checkAnyChildrenSelected,
	getAllSelectedCitationsText,
	unCheckAllCitationForRegulation,
} from 'features/RegulationInventory/RegulationGroup/RegulationGroupUtil';

import { RootState } from '../AITableView/AITableModules';
import StepHeader from '../StepHeader/StepHeader';

import styles from './RegulationSelectionDetail.module.scss';

const {
	simulationArena,
	regulationSummary,
	obligationToPolicy,
	plainEnglishSummary,
	obligationAndControl,
} = rrcpDashboardRoutes;

const { checkboxCheckedcolor, checkboxUncheckedcolor } = styles;

const { REGULATION_SUMMARY, OBLIGATION_TO_POLICY, PLAIN_ENGLISH_SUMMARY } =
	AI_SIMULATION_TYPES;

const { OK } = HTTP_STATUS_CODES;

const { NOTIFICATION_MSG } = COPYANDPASTE_TEXT;

const RegulationSelectionDetails = () => {
	const { simulationType, subPartId } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [regulation, setRegulation] = useState<ISubPartCitationDetails>(
		{} as ISubPartCitationDetails,
	);
	const [loading, setIsLoading] = useState<boolean>(false);
	const [selectAll, setSelectAll] = useState(false);
	const [displayNotification, setDisplayNotification] = useState(false);

	const [checkedCitations, setCheckedCitations] = useState<
		ISelectedCitationGroups[]
	>([]);
	const [selectedAccordians, setSelectedAccordians] = useState<
		ICitationDetailsElements[]
	>([]);
	const aiSummaryCharMaxLimit = useStringFeatureFlag(
		FeatureFlagsKeysEnum.AI_SUMMARY_CHAR_MAX_LIMIT,
	);
	const selectedAIRegulationText = useSelector(
		(state: RootState) => state.simulationArena.regulationText,
	);

	const selectedAIRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedRecipe,
	);

	const handleSelectAllCheckBox = (event: React.SyntheticEvent) => {
		setSelectAll((event.target as HTMLInputElement).checked);
		const citationArray: ISelectedCitationGroups[] = [];
		regulation?.applicabilityAssessment?.children?.map((res) => {
			if ((event.target as HTMLInputElement).checked) {
				const selectedArray = checkAllCitation(res, null);
				citationArray.push(...selectedArray);
			} else {
				const unSelectedArray = unCheckAllCitation(res, null);
				citationArray.push(...unSelectedArray);
			}
			return null;
		});
		if ((event.target as HTMLInputElement).checked) {
			updateSelectedCitationText(citationArray);
			setCheckedCitations(() => citationArray);
		} else {
			updateSelectedCitationText([]);
			setCheckedCitations([]);
		}
	};

	const handleAccordianChange = (
		val: ICitationDetailsElements,
		event: React.SyntheticEvent,
	) => {
		event.stopPropagation();
		if (selectedAccordians.includes(val)) {
			setSelectedAccordians((res) => res.filter((elem) => elem !== val));
		} else {
			setSelectedAccordians([...selectedAccordians, val]);
		}
	};

	const handleCheckBox = (
		event: React.SyntheticEvent,
		node: ICitationDetailsElements,
		rootNode?: ICitationDetailsElements | null,
	) => {
		event?.stopPropagation();
		if ((event?.target as HTMLInputElement).checked) {
			const checkedArray = checkAllCitation(node, rootNode, checkedCitations);
			updateSelectedCitationText(checkedArray);
			setCheckedCitations(checkedArray);
			if (
				checkAllChildrenSelected(
					checkedArray,
					regulation?.applicabilityAssessment,
				)
			) {
				setSelectAll(true);
			}
		} else {
			const newCitationArray = unCheckAllCitation(
				node,
				rootNode,
				checkedCitations,
			);
			if (
				!checkAllChildrenSelected(
					newCitationArray,
					regulation?.applicabilityAssessment,
				)
			) {
				setSelectAll(false);
			}
			updateSelectedCitationText(newCitationArray);
			setCheckedCitations(() => newCitationArray);
		}
	};
	useEffect(() => {
		const maxLimit = Number(
			aiSummaryCharMaxLimit ?? DEFAULT_AI_SUMMARY_MAX_LIMIT,
		);
		const withinTokenLimit = isWithinTokenLimit(
			selectedAIRegulationText,
			maxLimit,
		);
		if (!withinTokenLimit && selectedAIRegulationText.length > 0) {
			setDisplayNotification(true);
		} else {
			setDisplayNotification(false);
		}
	}, [selectedAIRegulationText]);
	useEffect(() => {
		if (subPartId) {
			fetchRegulationData(subPartId);
			dispatch(setRegulationText(''));
		}
	}, [subPartId]);

	const checkAllCitation = (
		node: ICitationDetailsElements,
		rootNode?: ICitationDetailsElements | null,
		checkedCitations: ISelectedCitationGroups[] = [],
	) => {
		const uniqArray = checkAllCitationForRegulation(
			checkedCitations,
			node,
			rootNode,
		);
		return uniqArray;
	};

	const updateSelectedCitationText = (
		citationList: ISelectedCitationGroups[],
	) => {
		const result = getAllSelectedCitationsText(
			regulation?.applicabilityAssessment?.children,
			citationList,
		);
		const citationText = result.join(' ');
		const regText = removeHTMLTags(citationText);
		dispatch(setRegulationText(regText));
	};

	const unCheckAllCitation = (
		node: ICitationDetailsElements,
		rootNode?: ICitationDetailsElements | null,
		checkedCitations: ISelectedCitationGroups[] = [],
	) => {
		const newCitationArray = unCheckAllCitationForRegulation(
			checkedCitations,
			node,
			rootNode,
		);
		return newCitationArray;
	};

	const fetchRegulationData = async (regulationId: string) => {
		setIsLoading(true);
		const resp: any = await getCitationDetails(regulationId || '');
		if (resp?.statusCode === OK) {
			const respData = resp?.responseBody;
			setRegulation(respData);
		}
		setIsLoading(false);
	};

	const handleClickNext = () => {
		switch (selectedAIRecipe) {
			case REGULATION_SUMMARY: {
				navigate(urlWithTenant(`/${simulationArena}/${regulationSummary}`));
				break;
			}
			case toSentenceCase(OBLIGATION_TO_POLICY): {
				navigate(urlWithTenant(`/${simulationArena}/${obligationToPolicy}`));
				break;
			}
			case PLAIN_ENGLISH_SUMMARY: {
				navigate(urlWithTenant(`/${simulationArena}/${plainEnglishSummary}`));
				break;
			}
			default: {
				navigate(urlWithTenant(`/${simulationArena}/${obligationAndControl}`));
			}
		}
	};

	const title: string = getAiSimulationTitle(simulationType ?? '');

	const accordianComp = () => (
		<div>
			{regulation?.applicabilityAssessment?.children?.map((res, index) => {
				const selectedNode = checkedCitations.some(
					(node) => node.citationId === res.id,
				);
				return (
					<AccordionComp
						expanded={selectedAccordians.includes(res)}
						className={styles['accordion-margin-btm']}
						parentDivStyle={styles['accordion-margin-btm']}
						onChange={(event) => handleAccordianChange(res, event)}
						isLeftAccordian
						key={res.id}
						summary={
							<Stack
								direction="row"
								justifyContent="space-between"
								width="100%"
							>
								<Stack direction="row" alignItems="center" width="100%">
									<Checkbox
										size="small"
										checked={selectedNode}
										data-testid={`inventory-checkbox${index}`}
										indeterminate={
											checkAnyChildrenSelected(checkedCitations, res) &&
											!checkAllChildrenSelected(checkedCitations, res)
										}
										onChange={(e) => handleCheckBox(e, res, null)}
										sx={{
											color: checkboxUncheckedcolor,
											'&.Mui-checked': {
												color: checkboxCheckedcolor,
											},
											'&.MuiCheckbox-indeterminate': {
												color: checkboxCheckedcolor,
											},
										}}
									/>
									<Typography
										className={styles['inventory-citation-heading']}
										data-testid="inventory-desc"
										onClick={(event) => handleAccordianChange(res, event)}
									>
										{res.description}
									</Typography>
								</Stack>
							</Stack>
						}
						details={
							<div>
								<CitationDescription
									currentNode={res}
									parentNode={res}
									isMultiCheckBoxRequired
									checkedCitations={checkedCitations}
									handleCheckBox={handleCheckBox}
								/>
							</div>
						}
					/>
				);
			})}
		</div>
	);

	return (
		<>
			<StepHeader title={title} Icon={HeaderIcon} />
			<Grid container spacing={2}>
				{loading ? (
					<Grid
						container
						spacing={2}
						justifyContent="center"
						alignItems="center"
						mt={1}
					>
						<Loader show />
					</Grid>
				) : (
					<>
						<Grid>
							<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
								<Stack
									direction="row"
									className={styles['btn-back-navigation']}
									onClick={() => navigate(-1)}
								>
									<BackIcon />
									<Typography className={styles['back-button-text']}>
										Back to search database
									</Typography>
								</Stack>
							</Grid>
						</Grid>

						<Grid
							item
							xs={12}
							sm={12}
							md={12}
							lg={12}
							xl={12}
							className={styles['citaion-details-wrapper']}
						>
							<Grid
								item
								xs={12}
								sm={12}
								md={12}
								lg={12}
								xl={12}
								className={styles['page-title']}
							>
								{regulation?.applicabilityAssessment?.title}
							</Grid>
							<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
								<hr className={styles['page-title-horizon']} />
							</Grid>
							{displayNotification && (
								<Grid
									item
									xs={12}
									sm={12}
									md={12}
									lg={12}
									xl={12}
									display="flex"
									className={styles['max-limit-notification']}
								>
									{NOTIFICATION_MSG}
								</Grid>
							)}
							{(regulation?.applicabilityAssessment?.children?.length || 0) >
								1 && (
								<Grid
									item
									xs={12}
									sm={12}
									md={12}
									lg={12}
									xl={12}
									display="flex"
									className={styles['select-all-checkbox-wrapper']}
								>
									<Checkbox
										data-testid="inventory-checkboxAll"
										size="small"
										checked={selectAll}
										onChange={handleSelectAllCheckBox}
										className={styles['select-all-checkbox']}
										sx={{
											color: checkboxUncheckedcolor,
											'&.Mui-checked': { color: checkboxCheckedcolor },
											'&.MuiCheckbox-indeterminate': {
												color: checkboxCheckedcolor,
											},
										}}
									/>
									<Typography className={styles['select-all-text']}>
										Select All
									</Typography>
								</Grid>
							)}

							<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
								{accordianComp()}
							</Grid>
						</Grid>
						<Grid
							container
							justifyContent="flex-end"
							className={styles['next-button']}
						>
							<ButtonComp
								btnType="primary"
								data-testid="Next-btn"
								disabled={checkedCitations.length === 0 || displayNotification}
								onClick={handleClickNext}
							>
								Next
							</ButtonComp>
						</Grid>
					</>
				)}
			</Grid>
		</>
	);
};

export default RegulationSelectionDetails;
